import { Fragment, type ReactNode } from "react";

/**
 * Renderizador mínimo de Markdown para os textos legais (docs/legal/*.md).
 * Cobre só o que esses documentos usam: títulos, parágrafos, listas, tabelas, negrito e linha horizontal.
 * Nada de HTML cru: o texto vem do repositório, mas a renderização é sempre por nós React.
 */

type Block =
  | { kind: "h"; level: 1 | 2 | 3; text: string }
  | { kind: "p"; lines: string[] }
  | { kind: "ul"; items: string[] }
  | { kind: "table"; header: string[]; rows: string[][] }
  | { kind: "hr" };

export function parseMarkdown(src: string): Block[] {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    const trimmed = line.trim();
    if (!trimmed) { i++; continue; }
    const h = /^(#{1,3})\s+(.*)$/.exec(trimmed);
    if (h) { blocks.push({ kind: "h", level: h[1]!.length as 1 | 2 | 3, text: h[2]! }); i++; continue; }
    if (/^-{3,}$/.test(trimmed)) { blocks.push({ kind: "hr" }); i++; continue; }
    if (trimmed.startsWith("|")) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i]!.trim().startsWith("|")) {
        const cells = lines[i]!.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
        if (!cells.every((c) => /^:?-{2,}:?$/.test(c))) rows.push(cells);
        i++;
      }
      const [header = [], ...body] = rows;
      blocks.push({ kind: "table", header, rows: body });
      continue;
    }
    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i]!.trim())) { items.push(lines[i]!.trim().replace(/^[-*]\s+/, "")); i++; }
      blocks.push({ kind: "ul", items });
      continue;
    }
    const para: string[] = [];
    while (i < lines.length) {
      const t = lines[i]!.trim();
      if (!t || /^(#{1,3})\s/.test(t) || /^[-*]\s+/.test(t) || t.startsWith("|") || /^-{3,}$/.test(t)) break;
      para.push(t);
      i++;
    }
    blocks.push({ kind: "p", lines: para });
  }
  return blocks;
}

/** `**negrito**` vira <strong>; o resto é texto puro. */
export function inline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, k) => (p.startsWith("**") && p.endsWith("**") ? <strong key={k}>{p.slice(2, -2)}</strong> : <Fragment key={k}>{p}</Fragment>));
}

export function Markdown({ source }: { source: string }) {
  const blocks = parseMarkdown(source);
  return (
    <>
      {blocks.map((b, k) => {
        switch (b.kind) {
          case "h": {
            const Tag = `h${b.level}` as "h1" | "h2" | "h3";
            return <Tag key={k}>{inline(b.text)}</Tag>;
          }
          case "hr":
            return <hr key={k} />;
          case "ul":
            return (
              <ul key={k}>
                {b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}
              </ul>
            );
          case "table":
            return (
              <div key={k} className="table-wrap">
                <table>
                  <thead>
                    <tr>{b.header.map((c, j) => <th key={j}>{inline(c)}</th>)}</tr>
                  </thead>
                  <tbody>
                    {b.rows.map((r, j) => (
                      <tr key={j}>{r.map((c, m) => <td key={m}>{inline(c)}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case "p":
            return (
              <p key={k}>
                {b.lines.map((l, j) => (
                  <Fragment key={j}>
                    {j > 0 && <br />}
                    {inline(l)}
                  </Fragment>
                ))}
              </p>
            );
        }
      })}
    </>
  );
}
